import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    Container,
    Typography,
    Stack,
    Divider,
    IconButton,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import { useNavigate, useSearchParams } from "react-router";
import { URLHandler } from "src/services/URLHandler";
import { MRTGrid } from "@foodpilot/foods";
import { createMRTColumnHelper, MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { TagValue, TagType } from "src/types/tag";
import { useGetAllTagTypes, useGetAllTagValues, useDeleteTagValue } from "src/api/client-api/tag";
import { useSnackbar } from "src/utils/useSnackbar";
import ActionsPopover from "src/components/ActionsPopover/ActionsPopover";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { GridText } from "src/components/Grid/GridText";

const columnHelper = createMRTColumnHelper<TagValue>();

export const TagValuesList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();

    // États
    const [paginationModel, setPaginationModel] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "sortOrder", desc: false }]);
    const [search, setSearch] = useState("");
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
    const [deleteDialog, setDeleteDialog] = useState<{
        open: boolean;
        tagValue: TagValue | null;
    }>({ open: false, tagValue: null });
    // Tracks the "value is in use" confirmation step. Opened only when the
    // first delete attempt comes back as 409 with a usage count.
    const [inUseDialog, setInUseDialog] = useState<{
        open: boolean;
        tagValue: TagValue | null;
        usageCount: number;
    }>({ open: false, tagValue: null, usageCount: 0 });

    // Filtre par type de tag
    const selectedTagTypeId = searchParams.get("tagType") || "";

    // Récupération des types de tags pour le filtre
    const { tagTypes = [] } = useGetAllTagTypes({
        itemsPerPage: 100,
        active: true,
    });

    // Récupération des valeurs de tags
    const {
        data: tagValuesResponse,
        isLoading,
        refetch,
    } = useGetAllTagValues({
        page: paginationModel.pageIndex + 1,
        itemsPerPage: paginationModel.pageSize,
        tagTypeId: selectedTagTypeId || undefined,
    });

    const tagValues = tagValuesResponse?.tagValues || [];
    const totalItems = tagValuesResponse?.totalItems || 0;

    // Legacy usage checking removed - tags are now managed differently

    const selectedTagValue = tagValues.find((tv: any) => tv.id === selectedRowId);

    // Hook pour supprimer une valeur de tag
    const deleteTagValueMutation = useDeleteTagValue();

    // Colonnes MRT
    const noValue = t("common.messages.deleted", "Type supprimé");
    const columns = [
        columnHelper.accessor("name", {
            header: "Nom",
            Cell: ({ cell }) => <GridText value={cell.getValue()} />,
            size: 200,
        }),
        columnHelper.accessor((row) => row.tagType, {
            id: "tagType.name",
            header: "Type de Tag",
            Cell: ({ row, cell }) => {
                const cellValue = cell.getValue();
                const rowData = row.original;
                // const color = (rowData.color !== "" ? rowData.color : rowData.tagType?.color) ?? "#ccc";
                const color = rowData.tagType?.color ?? "#ccc";
                return (
                    <Chip
                        label={cellValue?.name ?? noValue}
                        size="small"
                        sx={{
                            backgroundColor: color,
                            color: "white",
                            fontWeight: "bold",
                        }}
                    />
                );
            },
            size: 180,
        }),
        columnHelper.accessor("color", {
            header: "Couleur",
            Cell: ({ cell }) => {
                const color = cell.getValue() || cell.row.original.tagType?.color || "#ccc";
                return (
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            backgroundColor: color,
                            border: "1px solid #ccc",
                            borderRadius: 1,
                        }}
                    />
                );
            },
            size: 80,
        }),
        columnHelper.accessor((row) => row.tagType?.context, {
            id: "tagType.context",
            header: "Context",
            Cell: ({ cell }) => {
                const cellValue = cell.getValue();
                return <GridText value={cellValue} />;
            },
        }),
        columnHelper.accessor("description", {
            header: "Description",
            Cell: ({ cell }) => <GridText value={cell.getValue() || "-"} />,
            size: 300,
        }),
        columnHelper.accessor("sortOrder", {
            header: "Ordre",
            Cell: ({ cell }) => <GridText value={cell.getValue().toString()} />,
            size: 80,
        }),
    ];

    // Gestion du clic sur supprimer
    const handleDeleteClick = async () => {
        if (!selectedTagValue) return;

        setRowMenuAnchor(null);

        // Direct deletion - usage checking will be handled by the API
        setDeleteDialog({ open: true, tagValue: selectedTagValue });
    };

    // Actions du menu contextuel
    const menuItems: MenuItemType[] = [
        {
            icon: <EditIcon />,
            label: t("common.actions.edit", "Modifier"),
            onClick: () => {
                if (selectedRowId) {
                    setRowMenuAnchor(null);
                    navigate(URLHandler.settings.tags.tagValues.edit(selectedRowId));
                }
            },
        },
        {
            icon: <DeleteIcon />,
            label: t("common.actions.delete", "Supprimer"),
            onClick: handleDeleteClick,
        },
    ];

    // Backend message format: `Tag value "X" is currently used by N entities.`
    // We extract the trailing integer; if anything moves we degrade gracefully
    // to count = 0, which simply hides the count in the UI message.
    const extractUsageCount = (message: unknown): number => {
        if (typeof message !== "string") return 0;
        const match = message.match(/(\d+)\s+entit/i);
        return match ? parseInt(match[1], 10) : 0;
    };

    // First-step deletion. Backend returns 409 if entities still reference the
    // value; we then escalate to the in-use confirmation dialog instead of just
    // failing with a snackbar.
    const handleDelete = () => {
        if (!deleteDialog.tagValue) return;
        const tagValue = deleteDialog.tagValue;

        deleteTagValueMutation.mutate(
            { tagValueId: tagValue.id },
            {
                onSuccess: () => {
                    snackbar.setSnackbarProps({
                        message: t("settings.tags.tagValues.delete.success", "Valeur de tag supprimée avec succès"),
                        type: "success",
                    });
                    refetch();
                    setDeleteDialog({ open: false, tagValue: null });
                },
                onError: (error: any) => {
                    if (error?.response?.status === 409) {
                        // Hand off to the second-step "delete anyway" modal.
                        setDeleteDialog({ open: false, tagValue: null });
                        setInUseDialog({
                            open: true,
                            tagValue,
                            usageCount: extractUsageCount(error?.response?.data?.detail),
                        });
                        return;
                    }

                    snackbar.setSnackbarProps({
                        message: t(
                            "settings.tags.tagValues.delete.error",
                            "Erreur lors de la suppression de la valeur de tag",
                        ),
                        type: "error",
                    });
                    setDeleteDialog({ open: false, tagValue: null });
                },
            },
        );
    };

    // Second-step "delete anyway". Re-issues the delete with `force=true`, which
    // tells the backend to strip orphaned references from every taggable entity
    // before removing the value itself.
    const handleForceDelete = () => {
        if (!inUseDialog.tagValue) return;

        deleteTagValueMutation.mutate(
            { tagValueId: inUseDialog.tagValue.id, force: true },
            {
                onSuccess: () => {
                    snackbar.setSnackbarProps({
                        message: t("settings.tags.tagValues.delete.success", "Valeur de tag supprimée avec succès"),
                        type: "success",
                    });
                    refetch();
                    setInUseDialog({ open: false, tagValue: null, usageCount: 0 });
                },
                onError: () => {
                    snackbar.setSnackbarProps({
                        message: t(
                            "settings.tags.tagValues.delete.error",
                            "Erreur lors de la suppression de la valeur de tag",
                        ),
                        type: "error",
                    });
                    setInUseDialog({ open: false, tagValue: null, usageCount: 0 });
                },
            },
        );
    };

    // Changement de filtre par type de tag
    const handleTagTypeFilterChange = (event: SelectChangeEvent<string>) => {
        const newTagType = event.target.value;
        const newParams = new URLSearchParams(searchParams);

        if (newTagType) {
            newParams.set("tagType", newTagType);
        } else {
            newParams.delete("tagType");
        }

        setSearchParams(newParams);
        setPaginationModel({ pageIndex: 0, pageSize: 20 });
    };

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "5rem !important",
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography component="h1" variant="h2">
                    {t("settings.tags.tagValues.title", "Valeurs de Tags")}
                </Typography>
                <Box>
                    <IconButton onClick={() => refetch()} size="small" sx={{ marginRight: 1 }}>
                        <RefreshIcon />
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(URLHandler.settings.tags.tagValues.create())}
                        sx={(theme) => ({
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                            textTransform: "none",
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontSize: "0.875rem",
                            fontWeight: 700,
                            lineHeight: "1.5em",
                            boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                            "&:hover": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                            },
                        })}
                    >
                        {t("settings.tags.tagValues.create", "Nouvelle Valeur")}
                    </Button>
                </Box>
            </Stack>

            <Divider sx={{ mb: 4 }} />

            {/* Filtres */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{t("settings.tags.tagValues.type", "Type de Tag")}</InputLabel>
                    <Select
                        value={selectedTagTypeId}
                        onChange={handleTagTypeFilterChange}
                        label={t("settings.tags.tagValues.type", "Type de Tag")}
                    >
                        <MenuItem value="">
                            <em>{t("settings.tags.tagValues.allTypes", "Tous les types")}</em>
                        </MenuItem>
                        {tagTypes.map((tagType: TagType) => (
                            <MenuItem key={tagType.id} value={tagType.id}>
                                {tagType.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Box sx={{ mb: 4 }}>
                <MRTGrid
                    columns={columns}
                    data={tagValues}
                    enableRowActions
                    enableColumnResizing
                    enableSorting
                    enableGlobalFilter
                    enablePagination
                    manualPagination
                    manualSorting
                    manualFiltering
                    rowCount={totalItems}
                    state={{
                        isLoading,
                        pagination: paginationModel,
                        sorting,
                        globalFilter: search,
                    }}
                    onPaginationChange={setPaginationModel}
                    onSortingChange={setSorting}
                    onGlobalFilterChange={setSearch}
                    renderRowActions={({ row }) => (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRowId(row.original.id);
                                setRowMenuAnchor(e.currentTarget);
                            }}
                            sx={{ transform: "translateX(-4px)" }}
                        >
                            <MoreHoriz />
                        </IconButton>
                    )}
                />
            </Box>

            {/* Menu contextuel */}
            <ActionsPopover
                menuItems={menuItems}
                anchor={rowMenuAnchor}
                closeWindow={() => {
                    setRowMenuAnchor(null);
                    setSelectedRowId(null);
                }}
            />

            {/* Dialog de confirmation de suppression */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, tagValue: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t("common.actions.confirmDelete", "Confirmer la suppression")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(
                            "settings.tags.tagValues.confirmDelete",
                            'Êtes-vous sûr de vouloir supprimer la valeur de tag "{{name}}" ? Cette action est irréversible.',
                            { name: deleteDialog.tagValue?.name },
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, tagValue: null })} color="primary">
                        {t("common.actions.cancel", "Annuler")}
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteTagValueMutation.isLoading}
                    >
                        {t("common.actions.delete", "Supprimer")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Second-step dialog: shown when the backend returns 409 because the
                value is still referenced by entities. Lets the user proceed
                anyway, in which case the backend strips the references first. */}
            <Dialog
                open={inUseDialog.open}
                onClose={() => setInUseDialog({ open: false, tagValue: null, usageCount: 0 })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t("settings.tags.tagValues.inUseDialog.title", "This tag value is in use")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {inUseDialog.usageCount > 0 ?
                            t(
                                "settings.tags.tagValues.inUseDialog.body",
                                'The tag value "{{name}}" is currently used by {{count}} entity(ies). Deleting it will also remove the reference from those entities. Do you want to continue?',
                                { name: inUseDialog.tagValue?.name, count: inUseDialog.usageCount },
                            )
                        :   t(
                                "settings.tags.tagValues.inUseDialog.bodyUnknownCount",
                                'The tag value "{{name}}" is currently used by other entities. Deleting it will also remove the reference from those entities. Do you want to continue?',
                                { name: inUseDialog.tagValue?.name },
                            )
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setInUseDialog({ open: false, tagValue: null, usageCount: 0 })}
                        color="primary"
                    >
                        {t("common.actions.cancel", "Annuler")}
                    </Button>
                    <Button
                        onClick={handleForceDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteTagValueMutation.isLoading}
                    >
                        {t("settings.tags.tagValues.inUseDialog.confirm", "Delete anyway")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};
